import { PetrafiIcon } from '@repo/ui/components'
import { signOutReason } from '@/hooks/queries/auth/useVerifySession'
import { messageCodeCopy } from '@/lib/messageCode'
import { getErrorMessage, isRateLimited } from '@/lib/queries'
import { createHonoClient, type HonoClient } from '@/stores/api'
import {
  getSessionToken,
  getTenant,
  setPendingTenantSelection,
  setSessionToken,
  setTenant,
  type TenantInfo,
} from '@/stores/session'
import type { OtpChannel } from '@repo/constants'
import type { InferResponseType } from 'hono/client'
import { Button, Input, Label, RadioGroup } from '@repo/ui/components'
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router'
import { AlertCircle, ArrowLeft, LoaderCircleIcon, Mail, MessageSquare } from 'lucide-react'
import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'

const loginSearchSchema = z.object({
  redirect: z.string().optional(),
  email: z.string().email().optional(),
  reason: z
    .string()
    .optional()
    .transform((reason) => signOutReason({ message: reason }))
    .catch(undefined),
})

export const Route = createFileRoute('/login')({
  validateSearch: loginSearchSchema,
  beforeLoad: async ({ context }) => {
    const tenant = getTenant()
    if (!getSessionToken() || !tenant) return

    // Validate session is still active before redirecting
    try {
      const api = createHonoClient(getSessionToken())
      await context.queryClient.fetchQuery({
        queryKey: ['me'],
        queryFn: async () => {
          const r = await api.users.me.$post()
          if (!r.ok) throw new Error('Invalid session')
          return r.json()
        },
        staleTime: 5_000,
      })
      throw redirect({
        to: '/carriers/$slug/driver-onboarding/dashboard',
        params: { slug: tenant.slug },
      })
    } catch (e) {
      if (e && typeof e === 'object' && 'isRedirect' in e) throw e // re-throw redirect
      // Stale/invalid session — clean up silently
      setSessionToken(null as unknown as string)
      setTenant(null as unknown as TenantInfo)
    }
  },
  component: LoginPage,
})

type TenantOption = { id: string; name: string; slug: string; driverStatus?: string | null }
// Named rather than inlined: the three literals appear at nine call sites
// between the state, the guards and the back buttons, and a typo in any of them
// is a screen that silently never renders.
const STEP = {
  EMAIL: 'email',
  CHANNEL: 'channel',
  CODE: 'code',
} as const

type Step = (typeof STEP)[keyof typeof STEP]

// What the server decided about this send: which channel actually delivered, the
// last four digits when it texted, whether the other channel is on offer, and a
// MESSAGE code when a text was refused and the code was emailed instead. The
// server owns all of it — the client never infers a channel from a stored
// preference it cannot see.
// Inferred off the client rather than re-declared, so a new response field
// cannot be silently dropped by a hand-written copy (the app's convention —
// see `stores/session.ts`).
type Delivery = InferResponseType<HonoClient['portal-auth']['driver']['login']['$post']>

// What the address can receive on, asked before any code is sent so the choice
// can be offered up front rather than after the fact (AUTH-28).
type SignInOptions = InferResponseType<
  HonoClient['portal-auth']['driver']['sign-in-options']['$post']
>

// Unauthenticated client for login calls
const api = createHonoClient(null)

function LoginPage() {
  const { t } = useTranslation('ui')
  const { email: emailFromUrl, redirect: redirectTo, reason } = Route.useSearch()
  const navigate = useNavigate()
  const [step, setStep] = useState<Step>(STEP.EMAIL)
  const [email, setEmail] = useState(emailFromUrl ?? '')
  const [otp, setOtp] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(
    reason
      ? messageCodeCopy(t, { message: reason }, 'login.error.codes', 'login.error.generic')
      : null,
  )
  const [delivery, setDelivery] = useState<Delivery | null>(null)
  const [signInOptions, setSignInOptions] = useState<SignInOptions | null>(null)
  const [channelChoice, setChannelChoice] = useState<OtpChannel>('email')
  const otpInputRef = useRef<HTMLInputElement>(null)

  const completeLogin = (sessionToken: string, tenant: TenantOption) => {
    setSessionToken(sessionToken)
    setTenant(tenant)

    const defaultRedirect = `/carriers/${tenant.slug}/driver-onboarding/dashboard`
    const safeRedirect =
      redirectTo && redirectTo.startsWith('/') && !redirectTo.startsWith('//')
        ? redirectTo
        : defaultRedirect
    window.location.href = safeRedirect
  }

  // The rate limiter answers 429 with prose rather than a `MESSAGE.*` code, so
  // `messageCodeCopy` would miss and fall back to "An error occurred" —
  // telling a driver nothing about the one thing they can act on, which is to
  // wait. Branch on the status the body carries.
  const loginErrorCopy = (err: unknown) =>
    isRateLimited(err)
      ? t('login.error.rateLimited')
      : messageCodeCopy(t, err, 'login.error.codes', 'login.error.generic')

  // One path for the first send, the resend, and "send it the other way
  // instead" — three buttons that must not drift apart. `channel` omitted means
  // "use whatever this person last chose".
  const sendCode = async (channel?: OtpChannel) => {
    setIsLoading(true)
    setError(null)

    try {
      const res = await api['portal-auth'].driver.login.$post({
        json: { email, ...(channel && { channel }) },
      })

      setDelivery(await res.json())
      setOtp('')
      setStep(STEP.CODE)
      setTimeout(() => otpInputRef.current?.focus(), 100)
    } catch (err: unknown) {
      setError(loginErrorCopy(err))
    } finally {
      setIsLoading(false)
    }
  }

  // Ask what this address can receive on, then let them pick. With only one
  // channel reachable there is nothing to choose, so send immediately rather
  // than showing a one-option form.
  const loadOptions = async () => {
    setIsLoading(true)
    setError(null)

    try {
      const res = await api['portal-auth'].driver['sign-in-options'].$post({ json: { email } })
      const options = await res.json()
      setSignInOptions(options)

      if (!options.smsAvailable) {
        // No channel argument: the driver picked nothing, and `/login` reads an
        // explicit `channel` as an explicit pick and stores it. `resolveOtpChannel`
        // already answers email when nothing else is reachable, so this is
        // identical behaviour that leaves a standing `sms` default alone for when
        // the number, the verification or the credentials come back.
        await sendCode()
        return
      }

      setChannelChoice(options.defaultChannel)
      setStep(STEP.CHANNEL)
    } catch (err: unknown) {
      setError(loginErrorCopy(err))
    } finally {
      setIsLoading(false)
    }
  }

  const handleVerifyCode = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)
    setError(null)

    try {
      const res = await api['portal-auth'].driver['verify-code'].$post({
        json: { email, otp },
      })

      const data = await res.json()

      if ('sessionToken' in data) {
        completeLogin(data.sessionToken, data.tenant)
      } else if ('requireTenantSelection' in data) {
        // Stash the tempToken + tenants list and let /carriers render the
        // picker. Keeps the URL aligned with what the user sees.
        setPendingTenantSelection({ tempToken: data.tempToken, tenants: data.tenants })
        navigate({ to: '/carriers' })
      }
    } catch (err: unknown) {
      if (isRateLimited(err)) {
        setError(t('login.error.rateLimited'))
        return
      }
      // fetchWithThrow throws the parsed JSON body on non-2xx responses,
      // so we extract the error code from the thrown object.
      const code = getErrorMessage(err, '')
      if (code === 'INVALID_CODE') {
        setError(t('login.error.invalidCode'))
      } else if (code === 'PROFILE_NOT_VERIFIED') {
        setError(t('login.error.notVerified'))
      } else {
        setError(messageCodeCopy(t, err, 'login.error.codes', 'login.error.generic'))
      }
    } finally {
      setIsLoading(false)
    }
  }

  const sentBySms = delivery?.channel === 'sms'

  // Channel chooser — only reached when both channels are actually reachable.
  if (step === STEP.CHANNEL) {
    return (
      <div className="flex min-h-svh flex-col items-center justify-center gap-6 p-6">
        <div className="flex items-center gap-2">
          <PetrafiIcon className="size-10 text-primary" />
          <span className="text-2xl font-bold">{t('driverPortal')}</span>
        </div>
        <div className="w-full max-w-sm space-y-6">
          <div className="text-center">
            <h1 className="text-xl font-bold">{t('login.channel.title')}</h1>
            <p className="mt-1 text-sm text-muted-foreground">{t('login.channel.subtitle')}</p>
          </div>

          <form
            onSubmit={(e) => {
              e.preventDefault()
              void sendCode(channelChoice)
            }}
            className="space-y-4"
          >
            {error && (
              <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
                {error}
              </div>
            )}

            <RadioGroup
              value={channelChoice}
              onChange={(value: string) => setChannelChoice(value === 'sms' ? 'sms' : 'email')}
              aria-label={t('login.channel.title')}
            >
              <RadioGroup.Item
                value="sms"
                className="items-start gap-3 rounded-xl border border-input p-4 selected:border-primary selected:bg-primary/5"
              >
                <span className="flex flex-1 flex-col text-left">
                  <span className="flex items-center gap-2 font-semibold">
                    <MessageSquare className="size-4 text-primary" />
                    {t('login.channel.sms.title')}
                  </span>
                  <span className="mt-0.5 text-sm text-muted-foreground">
                    {t('login.channel.sms.detail', { hint: signInOptions?.phoneHint ?? '' })}
                  </span>
                </span>
              </RadioGroup.Item>

              <RadioGroup.Item
                value="email"
                className="items-start gap-3 rounded-xl border border-input p-4 selected:border-primary selected:bg-primary/5"
              >
                <span className="flex flex-1 flex-col text-left">
                  <span className="flex items-center gap-2 font-semibold">
                    <Mail className="size-4 text-primary" />
                    {t('login.channel.email.title')}
                  </span>
                  <span className="mt-0.5 text-sm break-all text-muted-foreground">{email}</span>
                </span>
              </RadioGroup.Item>
            </RadioGroup>

            <Button type="submit" className="w-full" isDisabled={isLoading}>
              {isLoading && <LoaderCircleIcon className="animate-spin" />}
              {t('login.channel.submit')}
            </Button>

            <Button
              variant="ghost"
              size="sm"
              className="w-full"
              onPress={() => {
                setStep(STEP.EMAIL)
                setError(null)
              }}
            >
              <ArrowLeft className="size-3" />
              {t('login.channel.changeAccount')}
            </Button>
          </form>
        </div>
      </div>
    )
  }

  // OTP verification
  if (step === STEP.CODE) {
    return (
      <div className="flex min-h-svh flex-col items-center justify-center gap-6 p-6">
        <div className="flex items-center gap-2">
          <PetrafiIcon className="size-10 text-primary" />
          <span className="text-2xl font-bold">{t('driverPortal')}</span>
        </div>
        <div className="w-full max-w-sm space-y-6">
          <div className="text-center">
            <h1 className="text-xl font-bold">
              {sentBySms ? t('login.otp.titleSms') : t('login.otp.titleEmail')}
            </h1>
            <p className="mt-1 text-sm text-muted-foreground">
              {sentBySms
                ? // From `signInOptions`, not the send response: `/login` no longer
                  // answers with the hint (AUTH-28 — one bounded source, not two).
                  t('login.otp.subtitleSms', { hint: signInOptions?.phoneHint ?? '' })
                : t('login.otp.subtitleEmail', { email })}
            </p>
            {/* The spam-folder nudge only makes sense for mail. */}
            <p className="mt-1 text-xs text-muted-foreground">
              {sentBySms ? t('login.otp.supersedeHint') : t('login.otp.spamHint')}
            </p>
          </div>

          {/* Not an error: the code was delivered, just not the way they asked.
              Rendered above the form so it reads before the input. */}
          {delivery?.smsFailure && (
            <div className="rounded-md bg-amber-50 p-3 text-sm text-amber-900">
              {t(`login.error.codes.${delivery.smsFailure}`, {
                defaultValue: t('login.error.codes.OTP_SMS_FAILED'),
              })}
            </div>
          )}

          <form onSubmit={handleVerifyCode} className="space-y-4">
            {error && (
              <div className="flex items-center gap-2 rounded-md bg-destructive/10 p-3 text-sm text-destructive">
                <AlertCircle className="size-4 shrink-0" />
                {error}
              </div>
            )}

            <div className="grid gap-2">
              <Label htmlFor="otp">{t('login.otp.label')}</Label>
              <Input
                ref={otpInputRef}
                id="otp"
                type="text"
                inputMode="numeric"
                placeholder={t('login.otp.placeholder')}
                value={otp}
                onChange={(e) => setOtp(e.target.value)}
                autoComplete="one-time-code"
                autoFocus
                required
              />
            </div>

            <Button type="submit" className="w-full" isDisabled={isLoading || !otp}>
              {isLoading && <LoaderCircleIcon className="animate-spin" />}
              {isLoading ? t('login.otp.verifying') : t('login.otp.verify')}
            </Button>

            <div className="flex items-center justify-between text-sm">
              {/* Lands on the chooser when one was shown, the email field when
                  it was skipped — so the label has to match what is behind us. */}
              <Button
                variant="ghost"
                size="sm"
                onPress={() => {
                  setStep(signInOptions?.smsAvailable ? STEP.CHANNEL : STEP.EMAIL)
                  setOtp('')
                  setError(null)
                }}
              >
                <ArrowLeft className="size-3" />
                {signInOptions?.smsAvailable
                  ? t('login.otp.changeChannel')
                  : t('login.otp.changeEmail')}
              </Button>
              <Button variant="link" size="sm" onPress={() => sendCode()} isDisabled={isLoading}>
                {t('login.otp.resend')}
              </Button>
            </div>
          </form>
        </div>
      </div>
    )
  }

  // Email form
  return (
    <div className="flex min-h-svh flex-col items-center justify-center gap-6 p-6">
      <div className="flex items-center gap-2">
        <PetrafiIcon className="size-10 text-primary" />
        <span className="text-2xl font-bold">{t('driverPortal')}</span>
      </div>
      <div className="w-full max-w-sm space-y-6">
        <div className="text-center">
          <h1 className="text-xl font-bold">{t('login.title')}</h1>
          <p className="mt-1 text-sm text-muted-foreground">{t('login.subtitle')}</p>
        </div>

        <form
          onSubmit={(e) => {
            e.preventDefault()
            void loadOptions()
          }}
          className="space-y-4"
        >
          {error && (
            <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">{error}</div>
          )}

          <div className="grid gap-2">
            <Label htmlFor="email">{t('login.email')}</Label>
            <Input
              id="email"
              type="email"
              placeholder={t('login.emailPlaceholder')}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>

          <Button type="submit" className="w-full" isDisabled={isLoading || !email}>
            {isLoading && <LoaderCircleIcon className="animate-spin" />}
            {isLoading ? t('login.submitting') : t('login.submit')}
          </Button>
        </form>
      </div>
    </div>
  )
}
